import Logger from 'utils/logger';
import { BrowserUtils } from 'utils/browser';

type Options = {
  audioVolume: number;
  videoVolume: number;
  onPlayingChange?: (isPlaying: boolean) => void;
  onBufferingChange?: (isBuffering: boolean) => void;
};

/** Below this drift the audio plays at the video's rate, untouched. */
const DRIFT_TOLERANCE_SECONDS = 0.1;

/**
 * Smaller drift is closed by nudging the audio's playback rate instead of
 * seeking. A seek is an audible gap, and in Safari a seek on an unbuffered
 * track takes long enough that the drift is back over a tight threshold by
 * the next timeupdate, so seek-based correction re-seeked in a loop and the
 * track cut out every half second.
 */
const RESEEK_THRESHOLD_SECONDS = 1;

/** A 5 % rate change closes the gap within seconds without being audible. */
const CATCH_UP_RATE = 0.05;

/**
 * The video acts as the master clock. Both stay plain media elements on
 * purpose: WebAudio's
 * createMediaElementSource outputs silence for cross-origin media without CORS
 * headers, and setting `crossOrigin` would turn a working no-cors media fetch
 * into a CORS request Instagram's CDN does not answer.
 *
 * Works without a video too, in which case the audio plays on its own.
 */
export function syncAudioToVideo(
  video: HTMLVideoElement | null,
  audio: HTMLAudioElement,
  { audioVolume, videoVolume, onPlayingChange, onBufferingChange }: Options,
): () => void {
  /** iOS ignores `volume`, so zero can only be reached through `muted`. */
  const canSetVolume = !BrowserUtils.isIOS();

  /**
   * Elsewhere `muted` is left to the video's owner — the preview's mute button
   * — and the added track follows it, so that button silences the whole mix.
   */
  const applyVolumes = () => {
    if (video) {
      video.volume = videoVolume / 100;

      if (!canSetVolume) {
        video.muted = videoVolume === 0;
      }
    }

    audio.volume = audioVolume / 100;
    audio.muted = audioVolume === 0 || (canSetVolume && !!video?.muted);
  };

  const applyLoop = () => {
    audio.loop =
      !!video &&
      Number.isFinite(video.duration) &&
      Number.isFinite(audio.duration) &&
      audio.duration < video.duration;
  };

  const handleAudioEnded = () => onPlayingChange?.(false);
  const handleBuffering = () => onBufferingChange?.(true);
  const handleBuffered = () => onBufferingChange?.(false);

  audio.addEventListener('ended', handleAudioEnded);
  audio.addEventListener('waiting', handleBuffering);
  audio.addEventListener('playing', handleBuffered);
  audio.addEventListener('canplay', handleBuffered);
  audio.addEventListener('loadedmetadata', applyLoop);

  const detachAudio = () => {
    audio.removeEventListener('ended', handleAudioEnded);
    audio.removeEventListener('waiting', handleBuffering);
    audio.removeEventListener('playing', handleBuffered);
    audio.removeEventListener('canplay', handleBuffered);
    audio.removeEventListener('loadedmetadata', applyLoop);
  };

  applyVolumes();
  applyLoop();

  if (!video) {
    return detachAudio;
  }

  /** A track shorter than the video loops, so its position wraps around. */
  const getExpectedAudioTime = () =>
    audio.loop && Number.isFinite(audio.duration) && audio.duration > 0
      ? video.currentTime % audio.duration
      : video.currentTime;

  const getDrift = () => getExpectedAudioTime() - audio.currentTime;

  const syncToVideo = () => {
    audio.playbackRate = video.playbackRate;
    audio.currentTime = getExpectedAudioTime();
  };

  const isOutOfSync = () => Math.abs(getDrift()) > DRIFT_TOLERANCE_SECONDS;

  /**
   * Correcting an audio element that is still seeking or buffering measures
   * a position that is about to change, so it waits until the audio plays.
   */
  const canCorrectDrift = () =>
    !audio.seeking && audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA;

  const startAudio = () => {
    applyLoop();
    applyVolumes();

    if (isOutOfSync()) {
      syncToVideo();
    }

    if (audio.paused) {
      audio.play().catch(Logger.error);
    }
  };

  const handlePlay = () => {
    startAudio();
    onPlayingChange?.(true);
  };

  const handlePause = () => {
    audio.pause();
    onPlayingChange?.(false);
  };

  /**
   * The audio is only paused, never the video: stalling the master clock as
   * well would leave the mix stopped with no event to restart it. The video
   * resuming re-seeks the audio back into place.
   */
  const handleVideoWaiting = () => {
    onBufferingChange?.(true);
    audio.pause();
  };

  const handleSeeking = () => audio.pause();

  const handleSeeked = () => {
    syncToVideo();

    if (!video.paused) {
      audio.play().catch(Logger.error);
    }
  };

  const handleTimeUpdate = () => {
    if (!canCorrectDrift()) {
      return;
    }

    const drift = getDrift();

    if (Math.abs(drift) > RESEEK_THRESHOLD_SECONDS) {
      syncToVideo();
      return;
    }

    audio.playbackRate =
      Math.abs(drift) > DRIFT_TOLERANCE_SECONDS
        ? video.playbackRate * (1 + Math.sign(drift) * CATCH_UP_RATE)
        : video.playbackRate;
  };

  video.addEventListener('play', handlePlay);
  video.addEventListener('playing', handlePlay);
  video.addEventListener('pause', handlePause);
  video.addEventListener('ended', handlePause);
  video.addEventListener('waiting', handleVideoWaiting);
  video.addEventListener('canplay', handleBuffered);
  video.addEventListener('seeking', handleSeeking);
  video.addEventListener('seeked', handleSeeked);
  video.addEventListener('timeupdate', handleTimeUpdate);
  video.addEventListener('loadedmetadata', applyLoop);
  /** Fired when the preview's mute button toggles the video. */
  video.addEventListener('volumechange', applyVolumes);

  if (!video.paused) {
    handlePlay();
  }

  return () => {
    detachAudio();
    video.removeEventListener('play', handlePlay);
    video.removeEventListener('playing', handlePlay);
    video.removeEventListener('pause', handlePause);
    video.removeEventListener('ended', handlePause);
    video.removeEventListener('waiting', handleVideoWaiting);
    video.removeEventListener('canplay', handleBuffered);
    video.removeEventListener('seeking', handleSeeking);
    video.removeEventListener('seeked', handleSeeked);
    video.removeEventListener('timeupdate', handleTimeUpdate);
    video.removeEventListener('loadedmetadata', applyLoop);
    video.removeEventListener('volumechange', applyVolumes);
  };
}
